import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import LocationService from '../../../shared/services/location/location.service';
import { ICountry, ILocation } from '../../../shared/services/location/location.model';
import { needsGeocodeAttention, geocodeAttentionReason } from '../../../shared/utils/location-geocode-utils';

interface IRow {
  id: string;
  label: string;
  postcode: string;
  countryId: string;
  /** Snapshot of the values as loaded, so only genuine edits are submitted. */
  originalPostcode: string;
  originalCountryId: string;
  needsAttention: boolean;
  reason: string;
}

const toRow = (location: ILocation): IRow => ({
  id: location.id,
  label: location.friendlyName,
  postcode: location.postcode ?? '',
  countryId: location.countryId ?? '',
  originalPostcode: location.postcode ?? '',
  originalCountryId: location.countryId ?? '',
  needsAttention: needsGeocodeAttention(location),
  reason: geocodeAttentionReason(location),
});

const isChanged = (row: IRow) =>
  row.postcode.trim() !== row.originalPostcode.trim() || row.countryId !== row.originalCountryId;

/**
 * Edit the address fields that decide where locations sit on the emissions map.
 *
 * Every location is listed, but the ones the map cannot place precisely are pulled to the top
 * so the work that actually matters is the work you see first; the rest sit behind "Show all".
 * Only edited rows are sent, so opening this and saving without touching anything is a no-op.
 */
export const UpdateUnmappedLocationsDialog: FunctionComponent<{
  open: boolean;
  locations: ILocation[];
  closeHandler: () => void;
  onUpdated: () => void;
}> = ({ open, locations, closeHandler, onUpdated }) => {
  const locationService = LocationService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);

  const [countries, setCountries] = useState<ICountry[]>([]);
  const [rows, setRows] = useState<IRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  useEffect(() => {
    locationService
      .getAllCountries()
      .then(setCountries)
      .catch((err) => console.log(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild on each open so a cancelled edit is not still sitting there next time, and the
  // "show all" section starts collapsed again.
  useEffect(() => {
    if (!open) return;
    setRows(locations.map(toRow));
    setShowErrors(false);
    setShowComplete(false);
  }, [open, locations]);

  const sortedCountries = useMemo(
    () => [...countries].sort((a, b) => a.name.localeCompare(b.name)),
    [countries],
  );

  const attentionRows = rows.filter((row) => row.needsAttention);
  const completeRows = rows.filter((row) => !row.needsAttention);
  const changedRows = rows.filter(isChanged);
  const invalidRows = changedRows.filter((row) => !row.countryId);

  const setRowValue = (id: string, field: 'postcode' | 'countryId', value: string) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const submitHandler = () => {
    if (invalidRows.length > 0) {
      setShowErrors(true);
      return;
    }
    if (changedRows.length === 0) {
      closeHandler();
      return;
    }
    setIsSubmitting(true);
    locationService
      .updateGeocodeDetails(changedRows.map((row) => ({
        id: row.id,
        // Empty means "no postcode", not an empty string to store.
        postcode: row.postcode.trim() === '' ? null : row.postcode.trim(),
        countryId: row.countryId,
      })))
      .then(() => {
        userNotificationsContext.openSnackbar({
          open: true,
          message: `${changedRows.length} ${screenLabelsContext.getLabel('location', { plural: changedRows.length !== 1 })} updated`,
        });
        setIsSubmitting(false);
        onUpdated();
        closeHandler();
      })
      .catch((err) => {
        console.log(err);
        setIsSubmitting(false);
        userNotificationsContext.openSnackbar({
          open: true,
          message: err?.response?.data?.message
            || `Could not save the changes. Please try again.`,
        });
      });
  };

  const renderRow = (row: IRow, showReason: boolean) => (
    <Box key={row.id}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.5 }} flexWrap='wrap' useFlexGap>
        <Typography variant='subtitle2'>
          <b>{row.label}</b>
        </Typography>
        {showReason && (
          <Chip label={row.reason} size='small' color='warning' variant='outlined' />
        )}
        {isChanged(row) && (
          <Chip label='Edited' size='small' color='primary' variant='outlined' />
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth size='small'>
          <InputLabel htmlFor={`postcode-${row.id}`}>Postcode</InputLabel>
          <OutlinedInput
            id={`postcode-${row.id}`}
            label='Postcode'
            name={`postcode-${row.id}`}
            value={row.postcode}
            onChange={(event) => setRowValue(row.id, 'postcode', event.target.value)}
          />
        </FormControl>
        <FormControl fullWidth size='small' error={showErrors && isChanged(row) && !row.countryId}>
          <InputLabel htmlFor={`countryId-${row.id}`}>Country</InputLabel>
          <Select
            id={`countryId-${row.id}`}
            label='Country'
            name={`countryId-${row.id}`}
            value={row.countryId}
            onChange={(event) => setRowValue(row.id, 'countryId', event.target.value)}
          >
            {sortedCountries.map((country) => (
              <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
            ))}
          </Select>
          {showErrors && isChanged(row) && !row.countryId && (
            <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
              Country is required
            </FormHelperText>
          )}
        </FormControl>
      </Stack>
    </Box>
  );

  const locationsLabel = screenLabelsContext.getLabel('location', { plural: true });

  // The title avoids the custom "location" term on purpose: getLabel returns an empty string
  // until the organisation's screen labels have loaded, which would title the dialog "Update ".
  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title='Update map information'
      cancelHandler={closeHandler}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      maxWidth='md'
      submitButtonText='Save'
      submitButtonLoadingText='Saving'
    >
      {/* Phrased without the custom "location" term: getLabel is empty until the organisation's
          screen labels load, which would render this as "places a  at its...". */}
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        A postcode places a pin at its postcode district on the map. Without one, the pin sits at
        the centre of the country. Postcodes outside the UK cannot be resolved, so those pins stay
        at country level.
      </Typography>

      {showErrors && invalidRows.length > 0 && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Country is required: {invalidRows.length} still to complete.
        </Alert>
      )}

      {attentionRows.length > 0 && (
        <>
          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            <b>Needs attention ({attentionRows.length})</b>
          </Typography>
          <Stack spacing={3}>
            {attentionRows.map((row) => renderRow(row, true))}
          </Stack>
        </>
      )}

      {attentionRows.length === 0 && (
        <Alert severity='success' sx={{ mb: 2 }}>
          Everything is placed as precisely as the available data allows.
        </Alert>
      )}

      {completeRows.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          {!showComplete ? (
            <Link
              component='button'
              type='button'
              underline='hover'
              onClick={() => setShowComplete(true)}
              sx={{ color: 'primary.main' }}
            >
              <Typography variant='body2'>
                Show all {completeRows.length} other {locationsLabel || 'entries'}
              </Typography>
            </Link>
          ) : (
            <>
              <Stack direction='row' alignItems='center' sx={{ mb: 2 }}>
                <Typography variant='subtitle1' sx={{ flex: 1 }}>
                  <b>Already placed ({completeRows.length})</b>
                </Typography>
                <Link
                  component='button'
                  type='button'
                  underline='hover'
                  onClick={() => setShowComplete(false)}
                  sx={{ color: 'primary.main' }}
                >
                  <Typography variant='body2'>Hide</Typography>
                </Link>
              </Stack>
              <Stack spacing={3}>
                {completeRows.map((row) => renderRow(row, false))}
              </Stack>
            </>
          )}
        </>
      )}
    </DialogWrapper>
  );
};
